import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { platform_search_api } from "../../DAL/Platform/Console";
import { C, KIND_LABEL, KIND_COLOR, panel } from "./consoleTheme";

/**
 * One search box, every organisation.
 *
 * WHY ONE BOX AND NOT TABS
 * Support is handed an email, a name, a booking reference or an id, and has no
 * idea which of those it is or which school it belongs to. Asking them to pick
 * the right entity tab first is asking them to already know the answer. The
 * school portal cannot do this at all — it can only ever search inside one
 * school, because that is the only thing it has access to.
 */
export default function Console() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const timer = useRef(null);
  const seq = useRef(0);

  const run = useCallback(async (term) => {
    if (term.trim().length < 2) { setResults(null); setError(null); return; }
    // Responses can land out of order; only the newest query may write state,
    // or a slow "mi" overwrites a fast "mia thompson".
    const mine = ++seq.current;
    setLoading(true);
    try {
      const res = await platform_search_api(term.trim());
      if (mine !== seq.current) return;
      if (res?.code === 200) { setResults(res); setError(null); }
      else setError(res?.message || "Search failed");
    } catch (e) {
      if (mine === seq.current) setError(e?.message || "Search failed");
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => run(q), 250);
    return () => clearTimeout(timer.current);
  }, [q, run]);

  const open = (r) => {
    if (r.kind === "school") navigate(`/console/school/${r.id}`);
    else if (r.kind !== "booking") navigate(`/console/person/${r.id}`);
  };

  const groups = results
    ? Object.entries(results.results).filter(([, v]) => v.length)
    : [];

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "32px 28px", color: C.text }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <Header />

        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search every organisation — email, name, school or id"
          style={{
            width: "100%", padding: "14px 16px", fontSize: 15,
            background: C.panel, color: C.text,
            border: `1px solid ${C.borderMid}`, borderRadius: 10, outline: "none",
          }}
          onFocus={(e) => (e.target.style.borderColor = C.brand)}
          onBlur={(e) => (e.target.style.borderColor = C.border)}
        />

        <div style={{ height: 20, marginTop: 8, fontSize: 12, color: C.textFaint }}>
          {loading ? "Searching…"
            : error ? <span style={{ color: C.high }}>{error}</span>
            : results ? `${results.counts.total} result${results.counts.total === 1 ? "" : "s"}`
            : "Type at least two characters."}
        </div>

        {groups.map(([kind, rows]) => (
          <section key={kind} style={{ marginTop: 22 }}>
            <h2 style={{ fontSize: 11, letterSpacing: 1, textTransform: "uppercase", color: C.textDim, margin: "0 0 8px" }}>
              {KIND_LABEL[rows[0].kind] || kind} · {rows.length}
            </h2>
            <div style={{ ...panel, overflow: "hidden" }}>
              {rows.map((r, i) => (
                <Row key={r.id} r={r} first={i === 0} onOpen={() => open(r)} />
              ))}
            </div>
          </section>
        ))}

        {results && !groups.length && !loading && (
          <p style={{ color: C.textFaint, marginTop: 28 }}>
            Nothing matched “{results.query}”.
          </p>
        )}
      </div>
    </div>
  );
}

function Header() {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase",
          color: C.brand, border: `1px solid ${C.brand}`, borderRadius: 4, padding: "2px 7px",
        }}>
          XAir staff
        </span>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Console</h1>
      </div>
      {/* Says plainly what this is, because the whole point is that it is not
          the school's screen and never pretends to be. */}
      <p style={{ color: C.textDim, fontSize: 13, margin: "6px 0 0", maxWidth: 620 }}>
        The platform's own view across every organisation. Read-only — actions
        are requested individually and recorded against your name.
      </p>
    </div>
  );
}

function Row({ r, first, onOpen }) {
  const [hover, setHover] = useState(false);
  const clickable = r.kind !== "booking";
  return (
    <div
      onClick={clickable ? onOpen : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "11px 14px",
        borderTop: first ? "none" : `1px solid ${C.border}`,
        background: hover && clickable ? C.panelHi : "transparent",
        cursor: clickable ? "pointer" : "default",
      }}
    >
      <span style={{
        width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
        background: KIND_COLOR[r.kind] || C.textFaint,
      }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {r.name || r.email || String(r.id)}
        </div>
        {(r.email && r.name) || r.school ? (
          <div style={{ fontSize: 12, color: C.textFaint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {[r.name && r.email, r.school].filter(Boolean).join(" · ")}
          </div>
        ) : null}
      </div>
      {r.region && <Tag>{r.region}</Tag>}
      {r.role && <Tag>{r.role}</Tag>}
      {r.status && <Tag>{r.status}</Tag>}
      {r.active === false && <Tag color={C.high}>inactive</Tag>}
    </div>
  );
}

function Tag({ children, color }) {
  return (
    <span style={{
      fontSize: 11, color: color || C.textDim, border: `1px solid ${color || C.border}`,
      borderRadius: 4, padding: "1px 6px", whiteSpace: "nowrap", flexShrink: 0,
    }}>
      {children}
    </span>
  );
}
